// @file    bottom-nav.ts
// @created 2025-06-01 by Copilot
// @desc    Persistent bottom navigation bar for the I4U agent portal

import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';

interface NavItem {
  labelKey: string;
  icon: string;
  route: string;
  queryParams?: Record<string, string>;
  matchFn?: (url: string) => boolean;
}

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './bottom-nav.html',
  styleUrl: './bottom-nav.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomNav implements OnInit, OnDestroy {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private sub!: Subscription;

  currentUrl = '';

  navItems: NavItem[] = [
    {
      labelKey: 'NAV.HOME',
      icon: 'fa-solid fa-house',
      route: '/agent-hub',
      matchFn: (url) => url.startsWith('/agent-hub') && !url.includes('tab=notifications'),
    },
    {
      labelKey: 'NAV.MENU',
      icon: 'fa-solid fa-bars',
      route: '/',
      matchFn: (url) => url === '/' || url === '/home',
    },
    {
      labelKey: 'NAV.SALES',
      icon: 'fa-solid fa-handshake',
      route: '/application-policy-list',
      matchFn: (url) => url.startsWith('/application-policy-list') || url.startsWith('/application-detail') || url.startsWith('/application-create'),
    },
    {
      labelKey: 'NAV.NOTIFICATIONS',
      icon: 'fa-solid fa-bell',
      route: '/agent-hub',
      queryParams: { tab: 'notifications' },
      matchFn: (url) => url.includes('tab=notifications'),
    },
    {
      labelKey: 'NAV.PROFILE',
      icon: 'fa-solid fa-user',
      route: '/profile',
      matchFn: (url) => url.startsWith('/profile'),
    },
  ];

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.sub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      this.currentUrl = e.urlAfterRedirects;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  isActive(item: NavItem): boolean {
    return item.matchFn ? item.matchFn(this.currentUrl) : this.currentUrl.startsWith(item.route);
  }

  navigate(item: NavItem): void {
    this.router.navigate([item.route], { queryParams: item.queryParams ?? {} });
  }
}
